import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import axios from 'axios';
import { getToken } from '../../../../services/authService';

const RestartObservationPeriod = ({ userId, onSuccess, isInObservationPeriod }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Q20 form states
    const [startTime, setStartTime] = useState('');
    const [duration, setDuration] = useState('');
    const [observationStartDate, setObservationStartDate] = useState('');

    const startTimeOptions = Array.from({ length: 7 }, (_, i) => ({
        value: `${i + 6}:00`,
        label: `${i + 6}:00`,
    }));

    const durationOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 6,
        label: `${i + 6} ${t('questions.q20.hours')}`
    }));

    const getMinDateString = () => {
        const today = new Date();
        // If user is in observation period, minimum date is tomorrow
        if (isInObservationPeriod) {
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow.toISOString().split('T')[0];
        }
        // Otherwise, minimum date is today
        return today.toISOString().split('T')[0];
    };

    const handleOpen = () => {
        setOpen(true);
        setError('');
        // Reset form
        setStartTime('');
        setDuration('');
        setObservationStartDate('');
    };

    const handleClose = () => {
        setOpen(false);
        setError('');
    };

    const handleSubmit = async () => {
        if (!startTime || !duration || !observationStartDate) {
            setError(t('restartObservation.fillAllFields'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = getToken();
            const API_URL = process.env.REACT_APP_API_URL;
            
            // Call the restart observation period endpoint
            const restartData = {
                userId,
                startTime,
                duration: parseInt(duration),
                observationStartDate,
                isInCurrentPeriod: isInObservationPeriod // Pass this flag to backend
            };

            await axios.post(`${API_URL}/notifications/restart-observation-period`, restartData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            setLoading(false);
            handleClose();
            
            // Call success callback if provided
            if (onSuccess) {
                onSuccess();
            }
            
            // Reload the page to refresh data
            window.location.reload();
            
        } catch (error) {
            setLoading(false);
            console.error('Error restarting observation period:', error);
            
            // Check if it's an overlap error
            if (error.response?.data?.message?.includes('overlap')) {
                const errorData = error.response.data;
                // Pass the interpolation values to the translation function
                setError(
                    t('restartObservation.overlapError', {
                        start: errorData.existingPeriod?.start || 'unknown',
                        end: errorData.existingPeriod?.end || 'unknown'
                    })
                );
            } else {
                setError(t('restartObservation.error'));
            }
        }
    };

    // Show button always, but with different text based on status

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                startIcon={<RestartAlt />}
                onClick={handleOpen}
                sx={{
                    backgroundColor: '#007BFF',
                    '&:hover': {
                        backgroundColor: '#0056b3'
                    },
                    textTransform: 'none'
                }}
            >
                {isInObservationPeriod ? t('restartObservation.buttonInPeriod') : t('restartObservation.button')}
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {isInObservationPeriod ? t('restartObservation.titleInPeriod') : t('restartObservation.title')}
                    </Typography>
                </DialogTitle>
                
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                            {isInObservationPeriod ? t('restartObservation.descriptionInPeriod') : t('restartObservation.description')}
                        </Typography>
                        
                        {isInObservationPeriod && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {t('restartObservation.warningInPeriod')}
                            </Alert>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                                {t('questions.q20.title')}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                {t('questions.q20.instruction')}
                            </Typography>
                        </Box>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <TextField
                                label={t('questions.q20.observationPeriod')}
                                type="date"
                                value={observationStartDate}
                                onChange={(e) => setObservationStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: getMinDateString() }}
                                fullWidth
                            />
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>{t('questions.q20.startTime')}</InputLabel>
                            <Select
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                label={t('questions.q20.startTime')}
                            >
                                <MenuItem value="">
                                    <em>{t('questions.q20.selectStartTime')}</em>
                                </MenuItem>
                                {startTimeOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>{t('questions.q20.duration')}</InputLabel>
                            <Select
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                label={t('questions.q20.duration')}
                            >
                                <MenuItem value="">
                                    <em>{t('questions.q20.selectDuration')}</em>
                                </MenuItem>
                                {durationOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={handleClose} 
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading || !startTime || !duration || !observationStartDate}
                        sx={{
                            backgroundColor: '#007BFF',
                            '&:hover': {
                                backgroundColor: '#0056b3'
                            },
                            textTransform: 'none'
                        }}
                    >
                        {loading ? <CircularProgress size={24} /> : t('restartObservation.start')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RestartObservationPeriod;